import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-kampong-phluk-homestay.jpg";

const itinerary = [
  {
    time: "Day 1 · 1:30–2:30 PM",
    title: "Hotel Pick-up & Drive to Kampong Phluk",
    body:
      "Pick-up from your hotel in Siem Reap and a 45–60 minute private drive through the countryside to the boat pier at Kampong Phluk village.",
  },
  {
    time: "Day 1 · Afternoon",
    title: "Stilted Village & Flooded Forest",
    body:
      "Cruise through houses raised 8–10 metres on stilts, watch fishing-community life, visit the local pagoda and (in season) glide by canoe through the mangrove flooded forest.",
  },
  {
    time: "Day 1 · Sunset",
    title: "Sunset on Tonle Sap",
    body:
      "Open-water boat ride onto the great lake for sunset photography and the quiet, golden atmosphere unique to the floating village.",
  },
  {
    time: "Day 1 · Evening",
    title: "Homestay Check-in & Khmer Dinner",
    body:
      "Settle into your floating homestay or local stilt house, share a traditional Khmer dinner with your host family, and relax by the water before overnight stay.",
  },
  {
    time: "Day 2 · Sunrise",
    title: "Sunrise & Morning Village Life",
    body:
      "Wake for sunrise over Tonle Sap, take an early village walk or boat ride and watch fishermen returning with their catch.",
  },
  {
    time: "Day 2 · Morning",
    title: "Breakfast & Cultural Interaction",
    body:
      "Breakfast with the local family, visit the village school or market (if open), and learn about traditional fishing tools and daily routines.",
  },
  {
    time: "Day 2 · 11:00 AM–12:00 PM",
    title: "Boat & Drive Back to Siem Reap",
    body:
      "Late-morning boat ride back to the pier, private transfer to Siem Reap and drop-off at your hotel.",
  },
];

const includes = [
  "English-speaking driver",
  "Private transport",
  "Boat tickets",
  "Floating homestay accommodation (1 night)",
  "1 dinner, 1 breakfast & 1 lunch",
  "Drinking water",
];

const excludes = [
  "Personal expenses",
  "Extra drinks",
  "Tips for host family (optional)",
];

const essentials = [
  "Hat, sunglasses & sunscreen",
  "Insect repellent",
  "Binoculars",
  "Light, breathable clothing & a change of clothes",
];

const pricing = [
  { group: "1–2 people", price: "$340 – $385" },
  { group: "3–5 people", price: "$435 – $515" },
  { group: "6–10 people", price: "$567 – $699" },
];

const TourKampongPhlukHomestay = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Kampong Phluk stilt-house homestay floating village tour Tonlé Sap with French-speaking guide Cambodia"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 2 Days / 1 Night Floating Home Stay</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Kampong Phluk <em className="font-light">Floating Village</em>.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              Sleep in a stilt-house homestay on Tonle Sap, cruise the flooded forest, and watch sunset and sunrise wash across one of Cambodia's most extraordinary lake communities.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days / 1 Night</span>
              <span>Group · 1–10</span>
              <span>Pickup · 1:30–2:30 PM Day 1</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Authentic lake life on the edge of Tonle Sap.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Experience authentic lake life at Kampong Phluk on the edge of Tonle Sap Lake. Stay overnight in a local homestay, explore stilt houses, cruise through flooded forests, and enjoy peaceful sunset and sunrise over the water.
            </p>
            <p>
              Day 1 brings you into the village in the afternoon — boats threading between 8–10 metre stilt houses, a visit to the pagoda, and (in season) a quiet canoe paddle through the mangrove flooded forest before sunset on the open lake.
            </p>
            <p>
              Day 2 begins at sunrise with the village waking, fishermen returning with their catch, breakfast with your host family and a gentle morning of cultural interaction before the journey back to Siem Reap.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Two days, one night, seven stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar
              pricePerPerson={340}
              tourTitle="2 Days / 1 Night Kampong Phluk Floating Home Stay"
              pricingTiers={[
                { maxPax: 1, price: 340 },
                { maxPax: 2, price: 385 },
                { maxPax: 3, price: 435 },
                { maxPax: 4, price: 478 },
                { maxPax: 5, price: 515 },
                { maxPax: 6, price: 567 },
                { maxPax: 7, price: 595 },
                { maxPax: 8, price: 634 },
                { maxPax: 9, price: 667 },
                { maxPax: 10, price: 699 },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Private tour price (per group).
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((p) => (
              <div key={p.group} className="bg-background p-8 border border-border">
                <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">{p.group}</p>
                <p className="font-display text-3xl">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need on the lake.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Physical Effort</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                No long uphill hikes, but you should be fit enough to walk up to 1 km in hot conditions on flat or uneven ground. Within the capability of most healthy travellers comfortable with tropical climates.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather is expected throughout. Occasional showers are possible. Midday is hot; wetland sites can be humid. The pace is designed so you avoid touring through the worst of the heat.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine is similar to most Southeast Asian cooking but rather less spicy. Host families and lodges prepare a wide variety of good local food, with fresh bread widely available and a surprising range of local brews to try.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Bring With You</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourKampongPhlukHomestay;
